import { Stack, Text, Title, List, ThemeIcon, Anchor } from '@mantine/core';
import { IconScale, IconLock, IconPoint } from '@tabler/icons-react';

/**
 * Legal document sections, including Terms of Service and Privacy Policy.
 * Each section contains the document metadata and its content as a React component.
 */
export const LEGAL_SECTIONS = [
    {
        id: 'terms',
        label: 'Terms of Service',
        lastUpdated: '2/21/25',
        icon: IconScale,
        content: (
            <Stack gap="lg">
                <Text c="dimmed" lh={1.6}>
                    By accessing or using our Website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, please do not use our Website.
                </Text>

                <Title order={3} size="h3">1. Acceptance of Terms</Title>
                <Text c="dimmed" lh={1.6}>
                    By using kahootbot.net, you acknowledge that you have read, understood, and agreed to these Terms. We reserve the right to update or modify these Terms at any time, and continued use of the Website constitutes acceptance of the changes.
                </Text>

                <Title order={3} size="h3">2. Description of Services</Title>
                <Text c="dimmed" lh={1.6}>
                    Our Website provides tools, guides, and resources related to Kahoot automation, and strategies ("Services"). These Services are intended for educational and testing purposes only. We do not encourage or condone any misuse of our tools.
                </Text>

                <Title order={3} size="h3">3. User Responsibilities</Title>
                <Text c="dimmed" lh={1.6}>By using our Website and services, you agree that:</Text>
                <List spacing="sm" size="sm" center icon={
                    <ThemeIcon color="blue" size={20} radius="xl">
                        <IconPoint size={12} />
                    </ThemeIcon>
                }>
                    <List.Item>You are responsible for how you use the tools and resources provided.</List.Item>
                    <List.Item>You will not use our Services for malicious, illegal, or disruptive activities, including spamming Kahoot games or violating Kahoot's Terms of Service.</List.Item>
                    <List.Item>You will not attempt to hack, damage, or disrupt any online services beyond what is allowed under ethical testing and educational purposes.</List.Item>
                    <List.Item>You may not use our Website in an iframe or embed it on any other website without our explicit written permission.</List.Item>
                    <List.Item>You may not copy, modify, or repurpose our tools, content, or services for use on another website or platform.</List.Item>
                    <List.Item>Our Services are for personal and educational use only. You may not use our Website for commercial purposes, resale, or redistribution in any form.</List.Item>
                </List>

                <Title order={3} size="h3">4. Legal Compliance</Title>
                <Text c="dimmed" lh={1.6}>
                    Using third-party automation tools on platforms like Kahoot may violate their Terms of Service. We are not responsible for any bans, restrictions, or consequences resulting from your actions. You use our Website at your own risk.
                </Text>

                <Title order={3} size="h3">5. Disclaimer of Liability</Title>
                <Text c="dimmed" lh={1.6}>
                    We provide our tools and content "as is" without warranties of any kind. We are not responsible for technical issues, bans, service disruptions, or damages resulting from the use of our Website. Any reliance on our information is at your own discretion and risk.
                </Text>

                <Title order={3} size="h3">6. Third-Party Links</Title>
                <Text c="dimmed" lh={1.6}>
                    Our Website may contain links to third-party websites. We do not endorse or take responsibility for the content, privacy policies, or practices of any third-party services.
                </Text>

                <Title order={3} size="h3">7. Restrictions on Embedding & Unauthorized Use</Title>
                <List spacing="sm" size="sm" center icon={
                     <ThemeIcon color="red" size={20} radius="xl">
                        <IconPoint size={12} />
                    </ThemeIcon>
                }>
                    <List.Item><b>Personal Use Only:</b> Our Website is intended solely for personal and educational use. Commercial use, automated scraping, resale, or redistribution of any content, tools, or services is strictly prohibited.</List.Item>
                    <List.Item><b>No Embedding in Iframes:</b> You may not display our Website or any of its content within an iframe or embed it on another website without our prior written consent.</List.Item>
                    <List.Item><b>No Use on Other Websites:</b> You may not copy, distribute, or replicate any part of our Website for use on another website, platform, or service.</List.Item>
                </List>

                <Title order={3} size="h3">8. Termination of Access</Title>
                <Text c="dimmed" lh={1.6}>
                    We reserve the right to suspend or terminate access to our Website for users who violate these Terms or engage in abusive, illegal, or harmful behavior.
                </Text>

                <Title order={3} size="h3">9. Intellectual Property</Title>
                <Text c="dimmed" lh={1.6}>
                    All content, branding, and tools on this Website are owned by us or properly licensed. Unauthorized reproduction, distribution, or modification of our content is prohibited.
                </Text>

                <Title order={3} size="h3">10. Contact Information</Title>
                <Text c="dimmed" lh={1.6}>
                    If you have any questions about these Terms, you can contact us at hello@kahootbot.net.
                </Text>
            </Stack>
        )
    },
    {
        id: 'privacy',
        label: 'Privacy Policy',
        lastUpdated: '2/21/25',
        icon: IconLock,
        content: (
            <Stack gap="lg">
                <Text c="dimmed" lh={1.6}>
                    Your privacy is important to us. This Privacy Policy explains how we handle data collection, third-party services, and advertising networks. By using our Website, you agree to this policy.
                </Text>

                <Title order={3} size="h3">1. Information We Collect</Title>
                <Text c="dimmed" lh={1.6}>
                    We do not permanently store any personal information. However, we may temporarily cache certain non-personal data to ensure the proper functionality of our services. This may include session-based information necessary for maintaining smooth operation and preventing misuse. Any cached data is automatically cleared after a short period and is not retained for long-term storage or analysis.
                </Text>

                <Title order={3} size="h3">2. Third-Party Advertising & Data Collection</Title>
                <Text c="dimmed" lh={1.6}>
                    While we do not collect personal data, our Website displays advertisements provided by third-party ad networks. These networks may use cookies, tracking technologies, or similar methods to serve relevant ads and improve their services. We do not control the data collected by third-party advertisers.
                </Text>

                <Title order={3} size="h3">3. Use of Cloudflare CDN</Title>
                <Text c="dimmed" lh={1.6}>
                    We use Cloudflare Content Delivery Network (CDN) to enhance website performance, security, and availability. Cloudflare may automatically collect certain technical data, such as IP addresses, browser type, and device details, to provide its services. Cloudflare’s data collection is governed by its own privacy policy, which you can review at <Anchor href="https://www.cloudflare.com/privacypolicy/" target="_blank" c="blue">Cloudflare Privacy Policy</Anchor>.
                </Text>

                <Title order={3} size="h3">4. Cookies & Tracking Technologies</Title>
                <Text c="dimmed" lh={1.6}>
                    Our Website itself does not use cookies or tracking technologies, but third-party services such as advertising networks and Cloudflare may place cookies in your browser for analytics, security, and ad personalization purposes.
                </Text>
                <Text c="dimmed" lh={1.6}>
                    You can manage or disable cookies through your browser settings if you wish to limit tracking. You may also opt out of personalized ads using tools provided by networks like:
                </Text>
                <List spacing="xs" size="sm">
                    <List.Item><Anchor href="https://optout.aboutads.info/" target="_blank" c="blue">Digital Advertising Alliance (DAA) Opt-Out</Anchor></List.Item>
                    <List.Item><Anchor href="https://youradchoices.com/" target="_blank" c="blue">YourAdChoices</Anchor></List.Item>
                    <List.Item><Anchor href="https://optout.networkadvertising.org/" target="_blank" c="blue">Network Advertising Initiative (NAI) Opt-Out</Anchor></List.Item>
                </List>

                <Title order={3} size="h3">5. External Links</Title>
                <Text c="dimmed" lh={1.6}>
                    Our Website may contain links to third-party websites. We are not responsible for the privacy practices or content of external websites. Please review their respective privacy policies before interacting with them.
                </Text>

                <Title order={3} size="h3">6. Data Security</Title>
                <Text c="dimmed" lh={1.6}>
                    While we do not collect personal information, we take security seriously and implement measures to protect our Website from cyber threats, unauthorized access, and malicious attacks. Cloudflare provides an additional layer of security through its Web Application Firewall (WAF) and DDoS protection.
                </Text>

                <Title order={3} size="h3">7. Your Privacy Rights</Title>
                <Text c="dimmed" lh={1.6}>
                    Depending on your location, you may have privacy rights under laws such as the General Data Protection Regulation (GDPR) or California Consumer Privacy Act (CCPA). Since we do not collect personal data, we do not store or sell user information. However, you can review and manage third-party tracking by visiting their privacy settings.
                </Text>

                <Title order={3} size="h3">8. Changes to This Privacy Policy</Title>
                <Text c="dimmed" lh={1.6}>
                    We reserve the right to update or modify this Privacy Policy at any time. Any changes will be reflected on this page with an updated "Last Updated" date.
                </Text>

                <Title order={3} size="h3">9. Contact Information</Title>
                <Text c="dimmed" lh={1.6}>
                    If you have any questions or concerns about this Privacy Policy, please contact us at hello@kahootbot.net.
                </Text>
            </Stack>
        )
    }
];